import { mergeGuestActivity } from "../../clientViewRuntime/api/publicClientViewApi";
import { mapSdkError } from "../../clientViewRuntime/api/sdkClient";
import { CASAMAPA_CLIENT_VIEW_SLUG } from "../../clientViewRuntime/utils/casaMapaManifestEnrichment";
import {
  buildGuestMergePayload,
  clearPendingGuestMerge,
  mergeSummaryHasChanges,
  readPendingGuestMerge,
  shouldShowMergeRecoveryWarning,
} from "./buyerGuestMergeStorage";
import {
  trackBuyerGuestMergeCompleted,
  trackBuyerGuestMergeFailed,
  trackBuyerGuestMergeStarted,
} from "./buyerGuestMergeAnalytics";
import { trackBuyerProfileMergeApplied } from "./buyerProfileAnalytics";

let inFlightGuestMerge = null;

async function executeBuyerGuestMerge({
  slug,
  visitorId,
  authMethod = null,
  t = (key) => key,
}) {
  const pending = readPendingGuestMerge();
  const pendingForSlug = pending?.clientViewSlug === slug ? pending : null;
  const built = buildGuestMergePayload(slug, visitorId, pendingForSlug);

  trackBuyerGuestMergeStarted(slug, {
    authMethod,
    visitorIdPresent: Boolean(built.visitorId),
  });

  try {
    const body = await mergeGuestActivity(slug, {
      visitorId: built.visitorId,
      guestPayload: built.guestPayload,
      authMethod,
    });

    const merged = body.merged || {};
    trackBuyerGuestMergeCompleted(slug, merged, {
      authMethod,
      visitorIdPresent: Boolean(built.visitorId),
    });

    if (merged.quizIntent || merged.financingPrefs) {
      trackBuyerProfileMergeApplied(slug, merged, {
        authMethod,
        visitorIdPresent: Boolean(built.visitorId),
      });
    }

    clearPendingGuestMerge();

    const toastMessage = resolveMergeToastMessage(slug, merged, t);
    const recoveryWarning =
      isCasaMapaClientView(slug)
        ? null
        : shouldShowMergeRecoveryWarning(built.guestPayload, merged, {
            invalidListingIds: body.invalidListingIds,
          })
          ? t("automotive.guestMerge.recoveryWarning")
          : null;

    return {
      ok: true,
      merged,
      mergedAny: mergeSummaryHasChanges(merged),
      toastMessage,
      recoveryWarning,
    };
  } catch (error) {
    trackBuyerGuestMergeFailed(slug, {
      authMethod,
      visitorIdPresent: Boolean(built.visitorId),
    });
    const mapped = mapSdkError(error);
    return {
      ok: false,
      errorMessage: mapped.message || t(guestMergeCopyKey(slug, "errorFlash")),
    };
  }
}

export async function runBuyerGuestMerge({
  slug,
  visitorId,
  authMethod = null,
  t = (key) => key,
}) {
  if (inFlightGuestMerge) {
    return inFlightGuestMerge;
  }

  inFlightGuestMerge = executeBuyerGuestMerge({
    slug,
    visitorId,
    authMethod,
    t,
  }).finally(() => {
    inFlightGuestMerge = null;
  });

  return inFlightGuestMerge;
}

function isCasaMapaClientView(slug) {
  return String(slug || "").toLowerCase() === CASAMAPA_CLIENT_VIEW_SLUG;
}

function guestMergeCopyKey(slug, key) {
  return isCasaMapaClientView(slug)
    ? `realEstate.casaMapa.account.guestMerge.${key}`
    : `automotive.guestMerge.${key}`;
}

function resolveMergeToastMessage(slug, merged, t) {
  if (!mergeSummaryHasChanges(merged)) {
    return null;
  }

  const likes = merged.likes || 0;
  const savedSearches = merged.savedSearches || 0;
  const hasProfile =
    merged.quizIntent || merged.financingPrefs || (merged.compareListingIds || 0) > 0;

  if (likes > 0 && savedSearches > 0 && hasProfile) {
    return t(guestMergeCopyKey(slug, "toastLikesSearchesAndPrefs"));
  }
  if (likes > 0 && savedSearches > 0) {
    return t(guestMergeCopyKey(slug, "toastLikesAndSearches"));
  }
  if (savedSearches > 0 && hasProfile) {
    return t(guestMergeCopyKey(slug, "toastSearchesAndPrefs"));
  }
  if (savedSearches > 0) {
    return t(guestMergeCopyKey(slug, "toastSearches"));
  }
  if (hasProfile && likes > 0) {
    return t(guestMergeCopyKey(slug, "toastLikesAndPrefs"));
  }
  if (hasProfile) {
    return t(guestMergeCopyKey(slug, "toastPrefs"));
  }
  return t(guestMergeCopyKey(slug, "toastLikes"));
}
